// Copyright (c) Ping. All rights reserved.
//
// Web contacts route host. Mirrors `apps/desktop/src/shell/hosts/
// ContactsRouteHost.tsx` one-for-one — both wire the shared
// `<ContactsScreen>` from @ping/ui and inject platform-specific
// data adapters (web: `useContactsBundle` + `searchUsers`; desktop:
// AsyncStorage-backed stubs until /v1/contacts ships).

import { useCallback, useEffect, useMemo, useState } from 'react';

import { AddContactModal, type ContactDirectoryUser, type ContactsBundle, ContactsScreen, CreateFolderModal, useConversations } from '@ping/ui';

import { log } from '@core/log/log';
import { getUserProfile, type PeerProfile, resolveMediaUrl } from '@core/network';
import {
  type DeviceContact,
  importVCardViaFilePicker,
  isContactPickerSupported,
  loadDeviceContacts,
  mergeDeviceContacts,
  pickDeviceContacts,
} from '@features/contacts/deviceContacts';
import { useContactsBundle } from '@features/contacts/useContactsBundle';
import { useRouter } from '@shell/RouterContext';

// Local removal tombstone (parity with iOS `removedContactIds`). Rows DERIVED
// from conversations / device vCards have no server record to delete, so
// removing them must be suppressed locally or they re-derive on the next render.
// Device-local + persistent (localStorage); an explicit re-add clears the id.
const REMOVED_CONTACTS_KEY = 'com.ping.contacts.removed.v1';

function loadRemovedContactIds(): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(REMOVED_CONTACTS_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveRemovedContactIds(ids: Set<string>): void {
  try {
    globalThis.localStorage?.setItem(REMOVED_CONTACTS_KEY, JSON.stringify([...ids]));
  } catch {
    /* best-effort */
  }
}

function deviceContactToDirectory(c: DeviceContact): ContactDirectoryUser {
  return {
    id: `contact:${c.id}`,
    username: c.identifier,
    displayName: c.displayName,
    ...(c.avatarUri !== undefined ? { avatarUri: c.avatarUri } : {}),
  } as ContactDirectoryUser;
}

// Map the rich peer profile (`GET /v1/users/{account_id}/profile`) to the
// shared contact shape, resolving the avatar media id to a fetchable URL so
// `<ContactAvatar imageUri>` can load it.
async function peerProfileToDirectory(p: PeerProfile): Promise<ContactDirectoryUser> {
  const avatarUri = p.avatarMediaId ? await resolveMediaUrl(p.avatarMediaId) : undefined;
  return {
    id: p.accountId,
    username: p.accountId,
    displayName: p.displayName,
    ...(avatarUri ? { avatarUri } : {}),
    ...(p.jobTitle ? { role: p.jobTitle } : {}),
    ...(p.companyName ? { company: p.companyName } : {}),
  };
}

export function ContactsRouteHost(): JSX.Element {
  const router = useRouter();
  const { bundle, isLoading, removeContact, toggleFavourite, createFolder } = useContactsBundle();
  const { conversations } = useConversations();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  // Device contacts (vCard import / Contact Picker), persisted per-device.
  const [deviceRows, setDeviceRows] = useState<DeviceContact[]>(() => loadDeviceContacts());
  // Locally-removed contact ids (tombstone) — derived / device rows re-derive
  // otherwise. Persistent + device-local; explicit re-add clears an entry.
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => loadRemovedContactIds());
  const removeDerivedContact = useCallback((userId: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.add(userId);
      saveRemovedContactIds(next);
      return next;
    });
  }, []);
  const restoreContact = useCallback((userId: string) => {
    setRemovedIds((prev) => {
      if (!prev.has(userId)) return prev;
      const next = new Set(prev);
      next.delete(userId);
      saveRemovedContactIds(next);
      return next;
    });
  }, []);
  const handleImportContacts = useCallback(() => {
    void (async () => {
      const next = isContactPickerSupported()
        ? await pickDeviceContacts()
        : await importVCardViaFilePicker();
      setDeviceRows(next);
    })();
  }, []);

  // Derive contacts from existing 1:1 conversations so any peer the
  // user has already chatted with shows up in the contacts list.
  const convPeers = useMemo<ContactDirectoryUser[]>(
    () =>
      conversations
        .filter((c) => c.kind === 'direct' && c.peerAccountId !== undefined)
        .map(
          (c) =>
            ({
              id: c.peerAccountId!,
              username: c.peerAccountId!,
              displayName: c.name,
              ...(c.avatarUri !== undefined ? { avatarUri: c.avatarUri } : {}),
            }) as ContactDirectoryUser,
        ),
    [conversations],
  );

  // The single merged contact list the screen renders: directory contacts
  // first, then device (vCard) contacts and conversation peers that aren't
  // already present. Deduped by id.
  const mergedContacts = useMemo<ContactDirectoryUser[]>(() => {
    const base = bundle.contacts as unknown as ContactDirectoryUser[];
    const seen = new Set(base.map((c) => c.id));
    const extra: ContactDirectoryUser[] = [];
    // Derived (device/conversation-peer) rows are suppressed once tombstoned —
    // server-backed `base` rows are removed via `removeContact` instead.
    for (const c of deviceRows.map(deviceContactToDirectory)) {
      if (!seen.has(c.id) && !removedIds.has(c.id)) { seen.add(c.id); extra.push(c); }
    }
    for (const c of convPeers) {
      if (!seen.has(c.id) && !removedIds.has(c.id)) { seen.add(c.id); extra.push(c); }
    }
    return extra.length === 0 ? base : [...base, ...extra];
  }, [bundle.contacts, deviceRows, convPeers, removedIds]);

  // Resolve the selected user for the detail pane. Everything the list
  // renders is already in `mergedContacts`, so resolve from memory first
  // (instant, offline-safe) and only reach the network to enrich with the
  // richer profile fields — `GET /v1/users/{account_id}/profile` (role,
  // company, avatar). Directory search is intentionally NOT used here:
  // `q=""` violates the endpoint contract (required, minLength 1) and 400s
  // in prod, which stranded the detail pane on an infinite skeleton.
  const [resolvedUser, setResolvedUser] = useState<ContactDirectoryUser | null>(null);
  const [isResolvingUser, setIsResolvingUser] = useState(false);

  useEffect(() => {
    if (selectedUserId === null) {
      setResolvedUser(null);
      setIsResolvingUser(false);
      return;
    }
    const cached = mergedContacts.find((u) => u.id === selectedUserId) ?? null;
    // Show the in-memory record immediately; skeleton only when we have
    // nothing cached yet and a network resolve is still pending.
    setResolvedUser(cached);
    // Device contacts carry a synthetic `contact:` id the directory can't
    // resolve — don't hit the network for them.
    if (selectedUserId.startsWith('contact:')) {
      setIsResolvingUser(false);
      return;
    }
    setIsResolvingUser(cached === null);
    let cancelled = false;
    getUserProfile(selectedUserId)
      .then(async (profile) => {
        if (cancelled || profile === null) return;
        const enriched = await peerProfileToDirectory(profile);
        if (cancelled) return;
        // Keep the friendly conversation/device display name if the profile
        // came back without one (defensive).
        setResolvedUser((prev) => ({
          ...enriched,
          displayName: enriched.displayName || prev?.displayName || enriched.id,
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        log.error({
          event: 'contactDetail.load.failed',
          err: (err as Error).message,
        });
      })
      .finally(() => {
        if (!cancelled) setIsResolvingUser(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUserId, mergedContacts]);

  // The shared component types `bundle.contacts` as `ContactDirectoryUser`
  // from @ping/ui — structurally identical to the web `DirectoryUser`. Cast
  // the bundle through `ContactsBundle` carrying the merged contact list.
  const sharedBundle = useMemo<ContactsBundle>(
    () => ({ ...(bundle as unknown as ContactsBundle), contacts: mergedContacts }),
    [bundle, mergedContacts],
  );

  return (
    <>
      <ContactsScreen
        bundle={sharedBundle}
        isLoading={isLoading}
        selectedUserId={selectedUserId}
        selectedUser={resolvedUser}
        isSelectedUserLoading={isResolvingUser}
        onUserSelect={setSelectedUserId}
        onAddContact={() => setIsAddContactOpen(true)}
        onImportContacts={handleImportContacts}
        onMessageRequested={() => {
          // Jump to the chat route — the conversation will get
          // started from there via the same requestChat path.
          router.push('/chat');
        }}
        onRemoveContact={(userId) => {
          // Server-backed contact → DELETE /v1/contacts; derived/device row →
          // local tombstone so it doesn't re-derive. Do both: a contact can be
          // both a server row AND a conversation peer, and the tombstone also
          // suppresses the re-derive after the server delete.
          void removeContact(userId).catch((err) => {
            log.error({
              event: 'contacts.remove.failed',
              err: (err as Error).message,
            });
          });
          removeDerivedContact(userId);
          setSelectedUserId(null);
        }}
        onToggleFavourite={(userId) => {
          void toggleFavourite(userId).catch((err) => {
            log.error({
              event: 'contacts.favourite.failed',
              err: (err as Error).message,
            });
          });
        }}
        onCreateFolder={() => setIsCreateFolderOpen(true)}
      />

      <AddContactModal
        open={isAddContactOpen}
        onDismiss={() => setIsAddContactOpen(false)}
        onCreate={({ displayName, identifier }) => {
          // No server-side contact list yet — persist the manual contact to the
          // on-device store (same source as imported vCards) so it appears in
          // the list immediately and survives reload.
          const id = identifier.length > 0 ? identifier : `manual-${Date.now()}`;
          const next = mergeDeviceContacts([{ id, displayName, identifier }]);
          setDeviceRows(next);
          // Explicit re-add clears the tombstone for both the raw id and the
          // synthetic `contact:` device-row id, so a previously-removed peer
          // reappears.
          restoreContact(id);
          restoreContact(`contact:${id}`);
        }}
      />

      <CreateFolderModal
        open={isCreateFolderOpen}
        onDismiss={() => setIsCreateFolderOpen(false)}
        onCreate={(name) => {
          void createFolder(name).catch((err) => {
            log.error({
              event: 'contacts.createFolder.failed',
              err: (err as Error).message,
            });
          });
        }}
      />
    </>
  );
}
